import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Card,
  CardContent,
  LinearProgress,
  InputAdornment,
  Stack,
  Chip,
  Snackbar,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Audiotrack as AudiotrackIcon,
  Clear as ClearIcon,
  Help as HelpIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Numbers as NumbersIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import {
  createPodcastEpisode,
  fetchPodcastEpisodeById,
  updatePodcastEpisode,
} from "../../services/api";
import { useSnackbar } from "notistack";

// Separate interface for form state
interface FormState {
  title: string;
  shortDescription: string;
  longDescription: string;
  active: boolean;
  episodeNumber: number;
  audioFile?: File;
  existingAudioUrl?: string;
  audioDuration?: string;
  startTime?: string;
  endTime?: string;
  scheduleDays?: string;
}

// Interface for API submission
interface SubmissionData {
  title: string;
  shortDescription: string;
  longDescription: string;
  active: boolean;
  episodeNumber: number;
  podcastId: string;
  audioFile?: File;
  existingAudioUrl?: string;
  audioDuration?: string;
  startTime?: string;
  endTime?: string;
  scheduleDays?: string;
}

interface FormErrors {
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  episodeNumber?: string;
  audioFile?: string;
}

const PodcastEpisodeForm: React.FC = () => {
  const { podcastId, episodeId } = useParams<{
    podcastId: string;
    episodeId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!episodeId;
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<FormState>({
    title: "",
    shortDescription: "",
    longDescription: "",
    active: true,
    episodeNumber: 1,
    audioDuration: "00:00",
    startTime: "",
    endTime: "",
    scheduleDays: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [audioFileName, setAudioFileName] = useState<string>("");
  const [formTouched, setFormTouched] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [charCount, setCharCount] = useState({
    title: 0,
    shortDescription: 0,
    longDescription: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const {
    data: episode,
    isLoading: isEpisodeLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["episode", episodeId],
    queryFn: () => fetchPodcastEpisodeById(Number(episodeId)),
    enabled: isEditMode,
  });

  // Initialize audio player when we have an existing audio URL
  useEffect(() => {
    if (formData.existingAudioUrl && !audioPlayer) {
      const newPlayer = new Audio(formData.existingAudioUrl);
      newPlayer.addEventListener("ended", () => setIsPlaying(false));
      setAudioPlayer(newPlayer);
    }

    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.removeEventListener("ended", () => setIsPlaying(false));
      }
    };
  }, [formData.existingAudioUrl, audioPlayer]);

  useEffect(() => {
    if (episode) {
      setFormData({
        title: episode.title,
        shortDescription: episode.shortDescription,
        longDescription: episode.longDescription || "",
        active: episode.active,
        episodeNumber: episode.episodeNumber,
        existingAudioUrl: episode.audioUrl,
        audioDuration: episode.audioDuration || "00:00",
        startTime: episode.startTime ? formatTimeSpan(episode.startTime) : "",
        endTime: episode.endTime ? formatTimeSpan(episode.endTime) : "",
        scheduleDays: episode.scheduleDays || "",
      });
      if (episode.audioUrl) {
        setAudioFileName(episode.audioUrl.split("/").pop() || "");
      }
      setCharCount({
        title: episode.title.length,
        shortDescription: episode.shortDescription.length,
        longDescription: episode.longDescription?.length || 0,
      });
    }
  }, [episode]);

  // Helper function to format TimeSpan to HH:MM format
  const formatTimeSpan = (timeSpan: string): string => {
    // If timeSpan is in TimeSpan format like "00:30:00"
    if (timeSpan.includes(":")) {
      const parts = timeSpan.split(":");
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
      }
    }
    return timeSpan;
  };

  // Helper function to convert HH:MM to TimeSpan format for API
  const parseTimeInput = (timeString: string): string | undefined => {
    if (!timeString) return undefined;

    // Check if already in correct format
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeString)) {
      return timeString.includes(":") && timeString.split(":").length === 2
        ? `${timeString}:00` // Add seconds if not present
        : timeString;
    }

    return undefined;
  };

  const createFormData = (data: FormState): FormData => {
    const formData = new FormData();

    // Add basic fields
    formData.append("title", data.title);
    formData.append("shortDescription", data.shortDescription);
    formData.append("longDescription", data.longDescription);
    formData.append("active", String(data.active));
    formData.append("episodeNumber", String(data.episodeNumber));
    formData.append("podcastId", podcastId!);
    formData.append("audioDuration", data.audioDuration || "00:00");

    // Add scheduling fields - even though they're hidden in UI, ensure proper formatting
    if (data.startTime) {
      formData.append("startTime", formatTimeForApi(data.startTime));
    }
    if (data.endTime) {
      formData.append("endTime", formatTimeForApi(data.endTime));
    }
    if (data.scheduleDays) {
      formData.append("scheduleDays", data.scheduleDays);
    }

    // Handle audio file
    if (data.audioFile) {
      formData.append("audioFile", data.audioFile);
      // When uploading a new file, set existingAudioUrl to a non-empty value to satisfy validation
      formData.append("existingAudioUrl", "new-upload");
    } else if (data.existingAudioUrl) {
      // When using an existing file
      formData.append("existingAudioUrl", data.existingAudioUrl);
    } else {
      // When neither file is uploaded nor existing URL is available
      // Set a placeholder to satisfy validation
      formData.append("existingAudioUrl", "placeholder");
    }

    return formData;
  };

  // Helper function to format time values for API (ensure HH:MM:SS format)
  const formatTimeForApi = (timeString: string): string => {
    // If it's already in HH:MM:SS format, return as is
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }

    // If it's in HH:MM format, add seconds
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      const [hours, minutes] = timeString.split(":");
      return `${hours.padStart(2, "0")}:${minutes}:00`;
    }

    // Default fallback
    return timeString;
  };

  const mutation = useMutation({
    mutationFn: (data: FormState) => {
      const formData = createFormData(data);
      if (episodeId) {
        return updatePodcastEpisode(Number(episodeId), formData);
      }
      return createPodcastEpisode(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["podcastEpisodes", podcastId],
      });
      navigate(`/podcasts/${podcastId}/episodes`);
      enqueueSnackbar(
        episodeId
          ? "Episode updated successfully"
          : "Episode created successfully",
        {
          variant: "success",
        }
      );
    },
    onError: (error) => {
      console.error("Error saving episode:", error);
      enqueueSnackbar("Error saving episode", { variant: "error" });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 100) {
      newErrors.title = "Title cannot exceed 100 characters";
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = "Short description is required";
    } else if (formData.shortDescription.length > 200) {
      newErrors.shortDescription =
        "Short description cannot exceed 200 characters";
    }

    if (!formData.episodeNumber || formData.episodeNumber < 1) {
      newErrors.episodeNumber = "Valid episode number is required";
    }

    if (!isEditMode && !formData.audioFile && !formData.existingAudioUrl) {
      newErrors.audioFile = "Audio file is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await mutation.mutateAsync(formData);
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    // Handle number inputs specifically
    if (name === "episodeNumber") {
      const numValue = parseInt(value);
      setFormData((prev) => ({
        ...prev,
        [name]: isNaN(numValue) ? 0 : numValue,
      }));
    } else {
      // Handle text inputs
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Update character count for text fields
      if (type === "text" || type === "textarea") {
        setCharCount((prev) => ({
          ...prev,
          [name]: value.length,
        }));
      }
    }

    // Mark form as touched
    if (!formTouched) {
      setFormTouched(true);
    }

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Stop any playing audio
      if (audioPlayer) {
        audioPlayer.pause();
        setIsPlaying(false);
      }

      setFormData((prev) => ({
        ...prev,
        audioFile: file,
        existingAudioUrl: undefined, // Clear existing audio URL when new file is selected
      }));
      setAudioFileName(file.name);

      // Create a new audio player for the selected file
      const newPlayer = new Audio(URL.createObjectURL(file));
      newPlayer.addEventListener("ended", () => setIsPlaying(false));
      setAudioPlayer(newPlayer);

      if (errors.audioFile) {
        setErrors((prev) => ({ ...prev, audioFile: undefined }));
      }

      setFormTouched(true);
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
    setFormTouched(true);
  };

  const handleCancel = () => {
    if (formTouched) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        navigate(`/podcasts/${podcastId}/episodes`);
      }
    } else {
      navigate(`/podcasts/${podcastId}/episodes`);
    }
  };

  const handleReset = () => {
    if (isEditMode && episode) {
      setFormData({
        title: episode.title,
        shortDescription: episode.shortDescription,
        longDescription: episode.longDescription || "",
        active: episode.active,
        episodeNumber: episode.episodeNumber,
        existingAudioUrl: episode.audioUrl,
        audioDuration: episode.audioDuration || "00:00",
        startTime: episode.startTime ? formatTimeSpan(episode.startTime) : "",
        endTime: episode.endTime ? formatTimeSpan(episode.endTime) : "",
        scheduleDays: episode.scheduleDays || "",
      });
      if (episode.audioUrl) {
        setAudioFileName(episode.audioUrl?.split("/").pop() || "");
      }
      setCharCount({
        title: episode.title.length,
        shortDescription: episode.shortDescription.length,
        longDescription: episode.longDescription?.length || 0,
      });
    } else {
      setFormData({
        title: "",
        shortDescription: "",
        longDescription: "",
        active: true,
        episodeNumber: 1,
        audioDuration: "00:00",
        startTime: "",
        endTime: "",
        scheduleDays: "",
      });
      setAudioFileName("");
      setCharCount({
        title: 0,
        shortDescription: 0,
        longDescription: 0,
      });
    }
    setErrors({});
  };

  const togglePlayback = () => {
    if (!audioPlayer) return;

    if (isPlaying) {
      audioPlayer.pause();
    } else {
      audioPlayer.play().catch((error) => {
        console.error("Error playing audio:", error);
      });
    }

    setIsPlaying(!isPlaying);
  };

  const clearAudioFile = () => {
    if (audioPlayer) {
      audioPlayer.pause();
      setIsPlaying(false);
    }

    setFormData((prev) => ({
      ...prev,
      audioFile: undefined,
      existingAudioUrl: undefined,
      // Keep the audioDuration unchanged
    }));
    setAudioFileName("");
    setAudioPlayer(null);
    setFormTouched(true);
  };

  if (isEpisodeLoading) {
    return (
      <Box sx={{ width: "100%", my: 4 }}>
        <LinearProgress />
        <Typography variant="subtitle1" sx={{ mt: 2, textAlign: "center" }}>
          Loading episode...
        </Typography>
      </Box>
    );
  }

  if (isError && error) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate(`/podcasts/${podcastId}/episodes`)}
          >
            Back to Episodes
          </Button>
        }
      >
        Error loading episode: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          to="/podcasts"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Podcasts
        </Link>
        <Link
          to={`/podcasts/${podcastId}/episodes`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Episodes
        </Link>
        <Typography color="text.primary">
          {isEditMode ? `Edit: ${formData.title}` : "Create Episode"}
        </Typography>
      </Breadcrumbs>

      {/* Form Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.paper",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton sx={{ mr: 1 }} onClick={handleCancel}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AudiotrackIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h5" component="h1">
              {isEditMode ? "Edit Episode" : "Create Episode"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={mutation.isPending}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={mutation.isPending}
            color="primary"
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Paper>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error:{" "}
          {(mutation.error as Error).message ||
            "Failed to save episode. Please try again."}
        </Alert>
      )}

      {mutation.isPending && <LinearProgress sx={{ mb: 3 }} />}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box component="form" noValidate>
                <TextField
                  fullWidth
                  required
                  label="Episode Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title}
                  disabled={mutation.isPending}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="textSecondary">
                          {charCount.title}/100
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  required
                  label="Short Description"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  error={!!errors.shortDescription}
                  helperText={errors.shortDescription}
                  disabled={mutation.isPending}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="textSecondary">
                          {charCount.shortDescription}/200
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Long Description"
                  name="longDescription"
                  value={formData.longDescription}
                  onChange={handleChange}
                  disabled={mutation.isPending}
                  margin="normal"
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="textSecondary">
                          {charCount.longDescription}/1000
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  type="number"
                  fullWidth
                  required
                  label="Episode Number"
                  name="episodeNumber"
                  value={formData.episodeNumber}
                  onChange={handleChange}
                  error={!!errors.episodeNumber}
                  helperText={errors.episodeNumber}
                  disabled={mutation.isPending}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <NumbersIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Audio File Field */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Audio File
              </Typography>

              {formData.existingAudioUrl || formData.audioFile ? (
                <Box sx={{ mb: 2 }}>
                  <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <AudiotrackIcon color="primary" fontSize="large" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">
                          {formData.audioFile
                            ? formData.audioFile.name
                            : audioFileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.audioFile
                            ? `${(
                                formData.audioFile.size /
                                (1024 * 1024)
                              ).toFixed(2)} MB`
                            : "Already uploaded"}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        {(formData.existingAudioUrl || formData.audioFile) && (
                          <Tooltip title={isPlaying ? "Pause" : "Play"}>
                            <IconButton
                              color="primary"
                              onClick={togglePlayback}
                            >
                              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove">
                          <IconButton color="error" onClick={clearAudioFile}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Card>
                </Box>
              ) : (
                <Box
                  sx={{
                    mb: 2,
                    p: 3,
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <AudiotrackIcon
                    color="action"
                    sx={{ fontSize: 40, mb: 1, opacity: 0.5 }}
                  />
                  <Typography variant="body1" gutterBottom>
                    No audio file selected
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    gutterBottom
                    display="block"
                  >
                    Upload MP3, WAV, or OGG files (max 50MB)
                  </Typography>
                </Box>
              )}

              <input
                accept="audio/*"
                style={{ display: "none" }}
                id="audio-file"
                type="file"
                onChange={handleFileChange}
                disabled={mutation.isPending}
              />
              <Stack direction="row" spacing={2} justifyContent="center">
                <label htmlFor="audio-file">
                  <Button
                    variant="contained"
                    component="span"
                    disabled={mutation.isPending}
                    startIcon={<UploadIcon />}
                  >
                    {formData.existingAudioUrl || formData.audioFile
                      ? "Replace Audio File"
                      : "Upload Audio File"}
                  </Button>
                </label>
              </Stack>

              {errors.audioFile && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.audioFile}
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Bottom Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          startIcon={<ArrowBackIcon />}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          startIcon={<SaveIcon />}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>Saving...</span>
            </Box>
          ) : isEditMode ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default PodcastEpisodeForm;
